<script setup lang="ts">
import { ref, computed } from 'vue';
import type { TypedColumns } from '@/types/QuasarOverrides';
import TableBadgesContainer from '@/components/Utilities/TableBadgesContainer.vue';
import {
    StudentCourseStatus,
    StudentExamStatus,
    type StudentCourseLockout,
} from '@/types/FlightSchoolStudent';
import TablePagination from '../Utilities/TablePagination.vue';
import type FlightSchool from '@/types/FlightSchool';
import StatusFilter from '@/components/Utilities/StatusFilter.vue';
import DateFilter from '@/components/Utilities/DateFilter.vue';
import type { DateFilter as DateFilterType } from '@/types/TableFilters';

export type MinimumRow = {
    isFocused: boolean;
    id: number;
    name: string;
    courseStatus: StudentCourseStatus;
    examStatus: StudentExamStatus;
    lockout: StudentCourseLockout | null;
    flightSchool: FlightSchool;
    courseAcronym: string;
    ratingAcronym: string;
    part: number;
    enrollDate: Date;
    examDate: Date | null;
    gradDate: Date | null;
    email: string;
};

export interface StudentLinkTableProps {
    rows: MinimumRow[];
    columns: TypedColumns;
    loading: boolean;
    sortBy?: string;
    showFilters?: boolean;
    enableDateFilter?: boolean;
    invisibleColumns?: string[];
}

const props = withDefaults(defineProps<StudentLinkTableProps>(), {
    sortBy: undefined,
    showFilters: true,
    enableDateFilter: false,
    invisibleColumns: () => [],
});

const filterOptions = [
    { header: 'Course Status', options: Object.values(StudentCourseStatus) },
    { header: 'Exam Status', options: Object.values(StudentExamStatus) },
];

const selectedStatusFilters = ref<string[]>([]);
const selectedDateFilters = ref<DateFilterType>(
    { filterBy: '', startDate: '', endDate: '' },
);
const quickFilter = ref<string|null>('');

/**
 * Filters based on three status categories: course status, exam status, and lockout status.
 * Filter for any status in each category (OR), but all categories must match (AND).
 */
const filterByStatus = (rows: MinimumRow[]): MinimumRow[] => {
    if (selectedStatusFilters.value.length === 0) {
        return rows;
    }

    const hasLockoutFilter = selectedStatusFilters.value.includes(StudentCourseStatus.locked);

    const remainingFilters = hasLockoutFilter
        ? selectedStatusFilters.value.filter((filter) => filter !== StudentCourseStatus.locked)
        : selectedStatusFilters.value;

    const courseStatusFilter = remainingFilters.filter(
        (filter) => Object.values(StudentCourseStatus).includes(filter as StudentCourseStatus),
    );

    const examStatusFilter = remainingFilters.filter(
        (filter) => Object.values(StudentExamStatus).includes(filter as StudentExamStatus),
    );

    return rows.filter((studentRow: MinimumRow): boolean => {
        // Determine if studentRow's status matches any course filters
        const hasMatchingCourseStatus = courseStatusFilter.length === 0
            || courseStatusFilter.includes(studentRow.courseStatus);

        // Determine if studentRow's exam status matches any exam filters
        const hasMatchingExamStatus = examStatusFilter.length === 0
            || examStatusFilter.includes(studentRow.examStatus);

        // Check lockout condition based on whether 'locked' was initially in filters
        const hasMatchingLockout = !hasLockoutFilter || studentRow.lockout !== null;

        return hasMatchingCourseStatus && hasMatchingExamStatus && hasMatchingLockout;
    });
};

const filterByDate = (rows: MinimumRow[]): MinimumRow[] => {
    if (selectedDateFilters.value.filterBy === '') {
        return rows;
    }

    const { filterBy, startDate, endDate } = selectedDateFilters.value;

    const start = new Date(startDate);
    const end = new Date(new Date(endDate).setHours(23, 59, 59, 999));

    return rows.filter((student: MinimumRow) => {
        switch (filterBy) {
        case 'Enrollment Date':
            return new Date(student.enrollDate) >= start && new Date(student.enrollDate) <= end;
        case 'Graduation Date':
            return student.gradDate && new Date(student.gradDate) >= start && new Date(student.gradDate) <= end;
        case 'Exam Date':
            return student.examDate && new Date(student.examDate) >= start && new Date(student.examDate) <= end;
        default:
            return false;
        }
    });
};

const filterByQuickFilter = (rows: MinimumRow[]): MinimumRow[] => {
    if (quickFilter.value === '' || quickFilter.value === null) {
        return rows;
    }

    const filter = quickFilter.value.toLowerCase();

    return rows.filter((student: MinimumRow) => student.name.toLowerCase().includes(filter)
        || student.email.toLowerCase().includes(filter)
        || student.id.toString().includes(filter));
};

const filteredRows = computed(() => {
    let { rows } = props;

    rows = filterByQuickFilter(filterByStatus(rows));

    return props.enableDateFilter ? filterByDate(rows) : rows;
});

const pagination = ref({
    page: 1,
    rowsPerPage: 15,
    sortBy: props.sortBy,
});

const getRowClass = (lockedOut: boolean): string => {
    const baseClass = 'fsum-student-row';

    return lockedOut ? `${baseClass} locked-out` : baseClass;
};

const visibleColumns = computed(() => props.columns.filter(
    (col) => !props.invisibleColumns?.includes(col.name),
).map((col) => col.name));

</script>
<template>
    <q-table
        v-model:pagination="pagination"
        :rows="filteredRows"
        :columns="columns"
        :loading="loading"
        :visible-columns="visibleColumns"
        row-key="name"
        class="fsum-student-table full-width"
        table-header-class="bg-fsum-all-blue text-fsum-all-blue text-bold"
        hide-pagination
    >
        <template
            v-if="showFilters"
            #top
        >
            <div class="row full-width q-gutter-x-md q-gutter-y-md print-hide">
                <q-input
                    v-model="quickFilter"
                    label="Search by name, email, or ID"
                    class="bg-white"
                    dense
                    outlined
                    clearable
                >
                    <template #append>
                        <q-icon name="search" />
                    </template>
                </q-input>
                <StatusFilter
                    :filter-options="filterOptions"
                    :table-loading="loading"
                    @apply-filters="(filteredStatuses) => selectedStatusFilters = filteredStatuses"
                />
                <DateFilter
                    v-if="props.enableDateFilter"
                    :table-loading="loading"
                    @apply-filters="(filter) => selectedDateFilters = filter"
                />
            </div>
        </template>
        <template #body="tableProps">
            <q-tr
                :props="tableProps"
                :class="getRowClass(tableProps.row?.lockout)"
            >
                <q-td
                    v-for="col in tableProps.cols"
                    :key="col.name"
                    :props="tableProps"
                    tabindex="0"
                    role="cell"
                >
                    <router-link
                        v-if="col.name=='name'"
                        class="student-record-link text-fsum-blue"
                        :to="{
                            name: 'studentRecord',
                            params: {
                                schoolSlug: tableProps.row.flightSchool.urlSlug,
                                courseAcronym: tableProps.row.courseAcronym.toLowerCase(),
                                part: tableProps.row.part,
                                ratingAcronym: tableProps.row.ratingAcronym.toLowerCase(),
                                cusId: tableProps.row.id,
                            },
                        }"
                    >
                        {{ col.value }}
                    </router-link>
                    <table-badges-container
                        v-else-if="col.name === 'courseStatus'"
                        :status="col.value"
                        :lockout="tableProps.row?.lockout"
                    />
                    <table-badges-container
                        v-else-if="col.name === 'examStatus'"
                        :status="col.value"
                    />
                    <template v-else>
                        {{ col.value }}
                    </template>
                </q-td>
            </q-tr>
        </template>
        <template #bottom>
            <table-pagination
                :pagination="pagination"
                :num-rows="filteredRows.length"
                pagination-label="Students per page"
                @update:pagination="(newPagination) => pagination = newPagination"
            />
        </template>
    </q-table>
</template>
<style scoped>
.student-record-link {
    text-decoration: none;
}
.select-page-students :deep(.q-field__native),
.select-page-students :deep(.q-select__dropdown-icon) {
    color: var(--fsum-dark-blue);
}
tr.locked-out {
    background-color: var(--fsum-badge-gray);
}
tr.locked-out td.q-td:before {
    background: var(--fsum-lockout-bg-gray);
}
:deep(.q-table__top) {
    background-color: var(--fsum-light-blue);
}
</style>
